import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import "../styles/home.css";
import "../styles/playable.css";
import { playableUrl } from "../playableBase";

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || "";
const ALLOWED_DOMAIN = "luckyspingames.com";
const SESSION_KEY = "lsg_playable_auth";

const GAMES = [
  {
    slug: "merge-only-v2",
    label: "Merge Only V2",
    exportLabel: "Merge_Only_V2",
    downloads: true,
    isNew: true, // NEW marks the latest batch; move it to the next new variants (keep them at the top)
    description:
      "Merge Only rebuilt without Cocos: same look, board and play (6\u00d78 board that starts full and only empties, blinking-pair hint with the hand), with sound and a mute button. About 1.6 MB per file vs 4.6 MB for the Cocos version.",
  },
  {
    slug: "generator-only-v2",
    label: "Generator Only V2",
    exportLabel: "Generator_Only_V2",
    downloads: true,
    isNew: true,
    description:
      "Generator Only rebuilt without Cocos: same look and play (full 6\u00d78 board, tap the generator under the board for a fresh level-1 item), with sound and a mute button. About 1.6 MB per file vs 4.6 MB.",
  },
  {
    slug: "timer-v2",
    label: "Generator + Timer V2",
    exportLabel: "Generator_Timer_V2",
    downloads: true,
    isNew: true,
    description:
      "Generator + Timer rebuilt without Cocos: 45s timer, +2s per merge, one treasure to win. Board now mixes six colourful chains (jewelry, birds, minton, netsuke, figures, blue-and-white) instead of two. Sound and mute button. About 2.1 MB per file.",
  },
  {
    slug: "moves-v2",
    label: "Generator + Move Limit V2",
    exportLabel: "Generator_MoveLimit_V2",
    downloads: true,
    isNew: true,
    description:
      "Generator + Move Limit rebuilt without Cocos: 10 moves, every merge, move, swap or generator tap costs one. Board now mixes six colourful chains (jade, yellow ceramics, egypt, greece, minton, figures). Sound and mute button. About 2.1 MB per file.",
  },
  {
    slug: "freeplay-v2",
    label: "Generator + Free Play V2",
    exportLabel: "Generator_FreePlay_V2",
    downloads: true,
    isNew: true,
    description:
      "Generator + Free Play rebuilt without Cocos: five-item chains, no timer or move limit, can't lose. Board now mixes six colourful chains (helmets, blue-and-white, dress, netsuke, egypt, birds). Sound and mute button. About 2.1 MB per file.",
  },
  {
    slug: "collector-orders",
    label: "On-Board Generator + Orders",
    exportLabel: "OnBoardGen_Orders",
    downloads: true,
    description:
      "Generator sits on the board (not below it); Collectors arrive with orders to fill. Round 1: one Collector; round 2: up to two at a time until four more orders are delivered. No timer \u2014 can't lose. Jewelry chain, terrace background.",
  },
  {
    slug: "collector-orders-timer",
    label: "On-Board Generator + Orders + Timer",
    exportLabel: "OnBoardGen_Orders_Timer",
    downloads: true,
    description:
      "Same as On-Board Generator + Orders, but in round 2 every Collector has a patience bar \u2014 smile, then worried, then sad if time runs out. One missed order ends the game on the fail screen.",
  },
  {
    slug: "merge-only",
    label: "Merge Only",
    exportLabel: "Merge_Only",
    downloads: true,
    description:
      "No generator \u2014 the 6\u00d78 board starts full and only ever empties. Tiles drop after a merge, nothing refills. This is the mechanic our best-performing playable used, rebuilt here for all three networks.",
  },
  {
    slug: "generator-only",
    label: "Generator Only",
    exportLabel: "Generator_Only",
    downloads: true,
    description:
      "Adds the generator under the board \u2014 tap it to drop a fresh level-1 item into a free cell when you're stuck. Full eight-item chains, no timer and no move limit.",
  },
  {
    slug: "timer",
    label: "Generator + Timer",
    exportLabel: "Generator_Timer",
    downloads: true,
    description:
      "Beat the clock: 45s timer, +2s per merge, generator on. Six-item chains, one treasure to win.",
  },
  {
    slug: "moves",
    label: "Generator + Move Limit",
    exportLabel: "Generator_MoveLimit",
    downloads: true,
    description:
      "Limited moves: 10 moves total \u2014 every merge, move, swap or generator tap costs one. Six-item chains, one treasure to win.",
  },
  {
    slug: "freeplay",
    label: "Generator + Free Play",
    exportLabel: "Generator_FreePlay",
    downloads: true,
    description:
      "Free play: shortened five-item chains, generator on, no timer, no move limit, can't lose. Fastest win: four merges.",
  },
];

// What each network wants, confirmed against their own creative specs (Sep 2026).
// `dir` is the folder under /playable/<slug>/ holding that network's build.
const NETWORKS = [
  {
    id: "unity",
    dir: "unity",
    label: "Unity Ads",
    // Must match the channel name in the playable project's make_variants.py,
    // so a file downloaded here and a file in build/variants/ are the same name
    // as well as the same bytes. Don't derive this from `label`.
    exportName: "Unity",
    zip: false,
    format: "Single self-contained HTML",
    note: "MRAID injected by the Unity SDK \u2014 no external files. CTA uses mraid.open().",
  },
  {
    id: "applovin",
    dir: "applovin",
    label: "AppLovin",
    exportName: "AppLovin",
    zip: false,
    format: "Single self-contained HTML",
    note: "MRAID injected by the AppLovin SDK \u2014 no external files. CTA uses mraid.open().",
  },
  {
    id: "mintegral",
    dir: "mintegral",
    label: "Mintegral",
    exportName: "Mintegral",
    zip: true,
    format: "ZIP containing index.html",
    note: "Mintegral calls gameStart / gameClose into the creative. CTA uses window.install().",
  },
];

function decodeJwt(token) {
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const json = decodeURIComponent(
      atob(base64)
        .split("")
        .map((c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0"))
        .join("")
    );
    return JSON.parse(json);
  } catch (e) {
    return null;
  }
}

function loadStoredUser() {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.exp && Date.now() / 1000 > parsed.exp) return null;
    return parsed;
  } catch (e) {
    return null;
  }
}

export default function Playable() {
  const [user, setUser] = useState(loadStoredUser);
  const [authError, setAuthError] = useState("");
  const [selected, setSelected] = useState(GAMES[0].slug);
  const [network, setNetwork] = useState(NETWORKS[0].id);
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [launched, setLaunched] = useState(false);
  const [frameLoading, setFrameLoading] = useState(true);
  const buttonRef = useRef(null);
  const activeGame = GAMES.find((g) => g.slug === selected) || GAMES[0];
  const activeNetwork = NETWORKS.find((n) => n.id === network) || NETWORKS[0];
  // Amplify only serves explicit <dir>/index.html paths verbatim: it redirects
  // /foo.html to /foo/ and rewrites unknown extensions to the SPA. So each build
  // lives at <slug>/<network>/index.html and the browser renames it on download.
  const sourceUrl = playableUrl(activeGame.slug, activeNetwork.dir, "index.html");
  const downloadName = `RF_${activeGame.exportLabel}_${activeNetwork.exportName}.${
    activeNetwork.zip ? "zip" : "html"
  }`;
  const previewSrc = activeGame.downloads
    ? playableUrl(activeGame.slug, "unity", "index.html")
    : playableUrl(activeGame.slug, "index.html");

  const handleDownload = async () => {
    setDownloading(true);
    setDownloadError("");
    try {
      const res = await fetch(sourceUrl);
      if (!res.ok) throw new Error(String(res.status));
      const html = await res.text();
      let blob;
      if (activeNetwork.zip) {
        // Mintegral: zip, folder and HTML must share one name, i.e.
        // RF_X_Mintegral.zip > RF_X_Mintegral/ > RF_X_Mintegral.html. An
        // index.html at the zip root never loads in their tester. Built here
        // rather than served as a .zip, which Amplify would rewrite to the SPA.
        const JSZip = (await import("jszip")).default;
        const zip = new JSZip();
        const stem = downloadName.replace(/\.zip$/, "");
        zip.file(`${stem}/${stem}.html`, html);
        blob = await zip.generateAsync({ type: "blob", compression: "DEFLATE" });
      } else {
        blob = new Blob([html], { type: "text/html" });
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = downloadName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch (err) {
      setDownloadError(
        "Couldn't prepare that file. Reload and try again \u2014 or take it from build/variants/ in the playable project."
      );
    }
    setDownloading(false);
  };

  useEffect(() => {
    document.title = "LSG Playable Ads Console";
  }, []);

  // On phones the player covers the whole screen; stop the page behind it
  // from scrolling while it is open.
  useEffect(() => {
    document.body.classList.toggle("playable-open", launched);
    return () => document.body.classList.remove("playable-open");
  }, [launched]);

  const handleCredentialResponse = (response) => {
    const payload = decodeJwt(response.credential);
    if (!payload) {
      setAuthError("Couldn't read that sign-in. Try again.");
      return;
    }
    const emailOk =
      payload.email &&
      payload.email_verified &&
      payload.email.toLowerCase().endsWith(`@${ALLOWED_DOMAIN}`);
    const hdOk = payload.hd === ALLOWED_DOMAIN;
    if (!emailOk && !hdOk) {
      setAuthError(`Sign in with a @${ALLOWED_DOMAIN} account.`);
      return;
    }
    const record = {
      email: payload.email,
      name: payload.name,
      picture: payload.picture,
      exp: payload.exp,
    };
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(record));
    setAuthError("");
    setUser(record);
  };

  useEffect(() => {
    if (user || !CLIENT_ID) return;

    // captured now so the cleanup below doesn't read a ref that may have moved on
    const buttonEl = buttonRef.current;
    const scriptId = "google-identity-services";
    let script = document.getElementById(scriptId);

    const init = () => {
      if (!window.google || !buttonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: CLIENT_ID,
        hd: ALLOWED_DOMAIN,
        callback: handleCredentialResponse,
        cancel_on_tap_outside: false,
      });
      window.google.accounts.id.renderButton(buttonRef.current, {
        theme: "outline",
        size: "large",
        text: "signin_with",
      });
    };

    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true;
      script.defer = true;
      script.onload = init;
      document.body.appendChild(script);
    } else {
      init();
    }

    // Once signed in, make sure nothing Google-rendered is left behind —
    // cancel any pending prompt and clear the button container.
    return () => {
      if (window.google && window.google.accounts) {
        window.google.accounts.id.cancel();
      }
      if (buttonEl) {
        buttonEl.innerHTML = "";
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const signOut = () => {
    sessionStorage.removeItem(SESSION_KEY);
    setUser(null);
    setLaunched(false);
    if (window.google) {
      window.google.accounts.id.disableAutoSelect();
    }
  };

  return (
    <div className="lsg-home playable-page">
      <nav className="lsg-nav">
        <div className="lsg-container lsg-nav-inner">
          <Link to="/" className="playable-nav-logo" aria-label="Lucky Spin Games home">
            <img
              src={`${process.env.PUBLIC_URL}/lsg/brand/lsg-horizontal-dark.svg`}
              alt="Lucky Spin Games"
              height="32"
            />
            <span className="playable-nav-subtitle">Playable Console</span>
          </Link>
          {user && (
            <div className="playable-user">
              <span>{user.email}</span>
              <button onClick={signOut} className="playable-signout">
                Sign out
              </button>
            </div>
          )}
        </div>
      </nav>

      <main className="playable-shell">
        <div className="lsg-container">
          {!CLIENT_ID ? (
            <div className="playable-gate" key="no-client">
              <h1>Playable console isn't configured yet</h1>
              <p>
                Set the <code>REACT_APP_GOOGLE_CLIENT_ID</code> environment
                variable in Amplify to turn on Google sign-in for this page.
              </p>
            </div>
          ) : !user ? (
            <div className="playable-gate" key="signed-out">
              <h1>LSG Playable Ads Console</h1>
              <p>Sign in with your @{ALLOWED_DOMAIN} account to continue.</p>
              <div ref={buttonRef} className="playable-google-btn" />
              {authError && <p className="playable-error">{authError}</p>}
            </div>
          ) : (
            <div className="playable-console" key="signed-in">
              <label className="playable-select-label" htmlFor="playable-select">
                Choose a variant
              </label>
              <select
                id="playable-select"
                className="playable-select"
                value={selected}
                onChange={(e) => {
                  setSelected(e.target.value);
                  setLaunched(false);
                  setDownloadError("");
                }}
              >
                {GAMES.map((game) => (
                  <option key={game.slug} value={game.slug}>
                    {game.isNew ? `NEW \u2014 ${game.label}` : game.label}
                  </option>
                ))}
              </select>

              {!launched ? (
                <div className="playable-info">
                  <h2>
                    {activeGame.label}
                    {activeGame.isNew && <span className="playable-new-badge">NEW</span>}
                  </h2>
                  <p>{activeGame.description}</p>
                  <button
                    className="playable-play-btn"
                    onClick={() => {
                      setFrameLoading(true);
                      setLaunched(true);
                    }}
                  >
                    Play
                  </button>
                </div>
              ) : (
                <div className="playable-player">
                  <div className="playable-player-bar">
                    <span className="playable-player-title">
                      {activeGame.isNew && <span className="playable-new-badge">NEW</span>}
                      {activeGame.label}
                    </span>
                    <button
                      type="button"
                      className="playable-close-btn"
                      aria-label="Close and go back to variant info"
                      onClick={() => {
                        setLaunched(false);
                        setFrameLoading(true);
                      }}
                    >
                      &times;
                    </button>
                  </div>
                  <div className="playable-frame-wrap">
                    {frameLoading && (
                      <div className="playable-frame-loading">
                        Loading {activeGame.label}&hellip;
                      </div>
                    )}
                    <iframe
                      key={selected}
                      className="playable-frame"
                      title={`Rare Find playable — ${selected}`}
                      src={previewSrc}
                      allow="autoplay"
                      onLoad={() => setFrameLoading(false)}
                    />
                  </div>
                  <button
                    className="playable-back-btn"
                    onClick={() => {
                      setLaunched(false);
                      setFrameLoading(true);
                    }}
                  >
                    &larr; Back to variant info
                  </button>
                </div>
              )}

              {activeGame.downloads ? (
                <div className="playable-delivery">
                  <h3>Deliver to an ad network</h3>
                  <div
                    className="playable-network-row"
                    role="radiogroup"
                    aria-label="Ad network"
                  >
                    {NETWORKS.map((n) => (
                      <button
                        key={n.id}
                        type="button"
                        role="radio"
                        aria-checked={n.id === network}
                        className={
                          "playable-network-chip" +
                          (n.id === network ? " is-active" : "")
                        }
                        onClick={() => {
                          setNetwork(n.id);
                          setDownloadError("");
                        }}
                      >
                        {n.label}
                      </button>
                    ))}
                  </div>
                  <p className="playable-network-format">
                    {activeNetwork.format}
                  </p>
                  <p className="playable-network-note">{activeNetwork.note}</p>
                  <button
                    type="button"
                    className="playable-download-btn"
                    onClick={handleDownload}
                    disabled={downloading}
                  >
                    {downloading
                      ? "Preparing\u2026"
                      : `Download for ${activeNetwork.label}`}
                  </button>
                  <p className="playable-download-name">{downloadName}</p>
                  {downloadError && (
                    <p className="playable-error">{downloadError}</p>
                  )}
                </div>
              ) : (
                <p className="playable-preview-only">
                  Preview only &mdash; this variant has no per-network build.
                </p>
              )}
            </div>
          )}
        </div>
      </main>

      <footer className="lsg-footer">
        <div className="lsg-container">
          <p className="lsg-footer-logo">Lucky Spin Games</p>
          <p className="lsg-footer-copy">
            &copy; {new Date().getFullYear()} Lucky Spin Games. All rights
            reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
